import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { InView } from 'react-intersection-observer';

import {
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';

import { SimilarBreedsBreederRowResource } from '../../constants/resource_types';
import useSimilarBreedsSeeAll from '../../hooks/useSimilarBreedsSeeAll';
import { sendResultGroupViewedEvent } from '../../utils/resultGroupViewedEvent';
import {
  SIMILAR_BREEDS_RESULTS_TYPES,
  SIMILAR_BREEDS_VARIANTS,
  SimilarBreedsResultsType,
  SimilarBreedsVariant
} from '../../utils/similarBreeds';
import SimilarBreedsSeeAllOverlay from './see_all/SimilarBreedsSeeAllOverlay';
import SimilarBreedsBreederList from './SimilarBreedsBreederList';
import SimilarBreedsPuppyCarousel from './SimilarBreedsPuppyCarousel';
import SimilarBreedsSectionFooter from './SimilarBreedsSectionFooter';

interface SimilarBreedsModuleProps {
  variant: SimilarBreedsVariant;
  resultsType: SimilarBreedsResultsType;
  /** The feed the module is anchored in - it renders in both. */
  view: string;
  puppies: any[];
  breeders: SimilarBreedsBreederRowResource[];
  /** The module's own filters, handed to the overlay unchanged. */
  filters: Record<string, unknown>;
  radiusInMiles?: number | null;
  totalPuppyCount?: number | null;
  className?: string | null;
}

const TREATMENT_VARIANTS: SimilarBreedsVariant[] = [
  SIMILAR_BREEDS_VARIANTS.PUPPY_CAROUSEL,
  SIMILAR_BREEDS_VARIANTS.BREEDER_LIST
];

// Which card the arm renders. The impression and click events share one
// `view_feature_location`, so this is what tells the two arms apart.
const VIEW_FEATURE_BY_RESULTS_TYPE = {
  [SIMILAR_BREEDS_RESULTS_TYPES.BREEDERS]: ANALYTICS_VIEW_FEATURES.BREEDER_CARD,
  [SIMILAR_BREEDS_RESULTS_TYPES.PUPPIES]: ANALYTICS_VIEW_FEATURES.PUPPY_CARD
};

// The module is anchored on page one of the feed and nowhere else, so its rows
// are always page one's. Threaded as a prop it could only ever be this.
const MODULE_PAGE_NUMBER = 1;

/**
 * How many puppies the visitor has already been shown, which is not the number
 * of cards: the carousel renders one puppy per card, while each breeder card
 * stands for that breeder's whole run of the badged breed. Counting cards would
 * mix the two units and overstate what is left to see.
 */
function shownPuppyCount(
  resultsType: SimilarBreedsResultsType,
  puppies: any[],
  breeders: SimilarBreedsBreederRowResource[]
) {
  if (resultsType === SIMILAR_BREEDS_RESULTS_TYPES.BREEDERS) {
    return breeders.reduce(
      (total, row) => total + (row.breeder_puppy_count || 0),
      0
    );
  }

  return puppies.length;
}

/** Renders the assigned arm. Both it and the rows arrive on one response. */
export default function SimilarBreedsModule({
  variant,
  resultsType,
  view,
  puppies,
  breeders,
  filters,
  radiusInMiles = null,
  totalPuppyCount = null,
  className = null
}: SimilarBreedsModuleProps) {
  const seeAll = useSimilarBreedsSeeAll();
  const queryClient = useQueryClient();
  const breedId = useSelector(({ server }: any) => server.breedId);

  // The rows this arm actually shows: one card per puppy, or one per breeder.
  const rows =
    resultsType === SIMILAR_BREEDS_RESULTS_TYPES.BREEDERS ? breeders : puppies;

  // One event for the whole module rather than the feed's groups of four. The
  // module is a fixed handful of cards shown together, so there is no second
  // group to scroll into.
  const trackInView = useCallback(
    (inView: boolean) => {
      if (!inView || !rows.length) {
        return;
      }

      sendResultGroupViewedEvent(queryClient, {
        breedId,
        resultsType,
        resultGroup: rows,
        position: 1,
        page: MODULE_PAGE_NUMBER,
        view,
        viewFeature: VIEW_FEATURE_BY_RESULTS_TYPE[resultsType],
        viewFeatureLocation:
          ANALYTICS_VIEW_FEATURE_LOCATIONS.SIMILAR_BREED_PUPPIES,
        // These rows belong to breeds other than the page's, and the module
        // runs on every eligible search - see `sendResultGroupViewedEvent`.
        fetchAvailability: false
      });
    },
    [queryClient, breedId, resultsType, rows, view]
  );

  const morePuppiesCount = useMemo(
    () =>
      Math.max(
        (totalPuppyCount || 0) -
          shownPuppyCount(resultsType, puppies, breeders),
        0
      ),
    [totalPuppyCount, resultsType, puppies, breeders]
  );

  // The overlay's cards write this page's identity into the sticky analytics
  // origin. Closing it is not a navigation, so without this a contact made
  // from the feed afterwards would still be attributed to the overlay. Keyed
  // to the overlay closing rather than unmounting, so clicking through to a
  // profile - which unmounts everything - keeps the origin it just set.
  const wasOverlayOpenRef = useRef(false);

  useEffect(() => {
    if (seeAll.isOpen) {
      wasOverlayOpenRef.current = true;
      return;
    }

    if (!wasOverlayOpenRef.current) {
      return;
    }

    wasOverlayOpenRef.current = false;
    setAnalyticsOrigin({ originView: view });
  }, [seeAll.isOpen, view]);

  if (!TREATMENT_VARIANTS.includes(variant)) {
    return null;
  }

  // No click event of its own, per the tracking spec: opening the overlay fires
  // `Breed Searched` for the page it opens, and that is what identifies the
  // engagement. A `Button Clicked` here would be a second, redundant record of
  // the same tap.
  function handleViewAllClick() {
    seeAll.open(resultsType);
  }

  const overlay = seeAll.isOpen ? (
    <SimilarBreedsSeeAllOverlay filters={filters} />
  ) : null;

  // The variant decides *whether* to render; `results_type` decides *which*
  // cards, since an override can change one without the other.
  const arm =
    resultsType === SIMILAR_BREEDS_RESULTS_TYPES.BREEDERS ? (
      <SimilarBreedsBreederList
        className={className}
        breeders={breeders}
        view={view}
        footer={
          <SimilarBreedsSectionFooter
            morePuppiesCount={morePuppiesCount}
            radiusInMiles={radiusInMiles}
            onViewAllClick={handleViewAllClick}
          />
        }
      />
    ) : (
      <SimilarBreedsPuppyCarousel
        className={className}
        puppies={puppies}
        view={view}
        // No count on this arm, per the design: the carousel's cards are the
        // puppies themselves, so the button sits alone.
        footer={
          <SimilarBreedsSectionFooter onViewAllClick={handleViewAllClick} />
        }
      />
    );

  return (
    <>
      {/* No `threshold`, unlike the feed's cards at 0.8: the breeder arm can
          stand taller than a phone viewport, and a fraction of it that never
          comes into view at once would drop the impression entirely. */}
      <InView triggerOnce onChange={trackInView}>
        {arm}
      </InView>
      {overlay}
    </>
  );
}
